import React from 'react';
import { ChevronDown, ChevronRight, TreePine, BarChart3, Edit3, CheckCircle, Target, Download, Clock, Zap } from 'lucide-react';
import { essToolboxData } from '../data/essToolboxData';

// ESS Toolbox Content Tab
export const ESSToolboxContent = () => {
  const [expandedItems, setExpandedItems] = React.useState({});

  const toggleExpand = (key) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-800">五大句型詳細結構與例句</h3>
      <p className="text-gray-600 text-sm">
        以下以五大句型為主軸，每一型再細分→再細分→再細分，每個層級都給多個完整例句，並括號註明句型結構。
      </p>
      
      {Object.entries(essToolboxData.sentencePatterns).map(([mainType, pattern]) => (
        <div key={mainType} className="border rounded-lg p-4 bg-white shadow-sm">
          <button
            onClick={() => toggleExpand(`detailed-${mainType}`)}
            className="flex items-center justify-between w-full text-left font-bold text-lg text-blue-700 mb-4"
          >
            <span>{pattern.name}（{pattern.chineseName}）</span>
            {expandedItems[`detailed-${mainType}`] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>
          
          {expandedItems[`detailed-${mainType}`] && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded border-l-4 border-blue-200">
                <div className="font-medium text-gray-800">{pattern.description}</div>
                <div className="text-sm text-blue-600 mt-1">
                  <code className="bg-blue-100 px-2 py-1 rounded">{mainType}</code>
                </div>
              </div>
              
              <div className="space-y-2">
                <h6 className="font-medium text-gray-700">Examples:</h6>
                {pattern.examples.map((example, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded border-l-4 border-blue-200">
                    <div className="font-medium text-gray-800">{example}</div>
                    <div className="text-sm text-blue-600 mt-1">
                      <code className="bg-blue-100 px-2 py-1 rounded">{mainType}</code>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Analysis Tab
export const AnalysisTab = ({ inputText, setInputText, analyzedSentences, isLoading, analyzePassage }) => {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          輸入英文段落：
        </label>
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="4"
          placeholder="When our display books are in poor condition, we of course would try to offer a new copy of these books to our customers."
        />
        <button
          onClick={analyzePassage}
          disabled={isLoading}
          className={`mt-2 px-4 py-2 rounded-lg transition-colors ${
            isLoading 
              ? 'bg-gray-400 text-white cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isLoading ? '分析中...' : '開始分析'}
        </button>
      </div>

      {analyzedSentences.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">句型分析結果</h3>
          {analyzedSentences.map((sentence) => (
            <div key={sentence.id} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="space-y-3">
                {/* Original Sentence */}
                <div className="p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                  <div className="font-medium text-gray-800 mb-1">原文句子:</div>
                  <div className="text-gray-700">{sentence.original}</div>
                </div>

                {/* Analysis Results */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="font-medium text-gray-800 mb-1">五大句型:</div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                      {sentence.type}
                    </span>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="font-medium text-gray-800 mb-1">分類:</div>
                    <span className="text-gray-700">{sentence.category}</span>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="font-medium text-gray-800 mb-1">細分:</div>
                    <span className="text-gray-700">{sentence.subCategory}</span>
                  </div>
                  
                                     <div className="p-3 bg-gray-50 rounded">
                     <div className="font-medium text-gray-800 mb-1">Legend結構:</div>
                     <div className="flex flex-wrap gap-1">
                       {(sentence.detailedAnalysis?.legendStructure || sentence.legend || '')
                         .split('+')
                         .map((part, index) => {
                           const trimmed = part.trim();
                           if (!trimmed) return null;
                           
                           // Extract the grammatical function from parentheses
                           const match = trimmed.match(/(.+)\(([^)]+)\)/);
                           if (match) {
                             const [, word, func] = match;
                             return (
                               <span key={index} className="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-mono">
                                 <span className="text-blue-900">{word.trim()}</span>
                                 <span className="text-blue-600 ml-1">({func})</span>
                               </span>
                             );
                           }
                           
                           return (
                             <span key={index} className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm font-mono">
                               {trimmed}
                             </span>
                           );
                         })}
                     </div>
                   </div>
                </div>

                {/* AI Detailed Analysis */}
                {sentence.detailedAnalysis && (
                  <div className="p-3 bg-green-50 rounded border-l-4 border-green-400">
                    <div className="text-sm font-medium text-green-800 mb-2">AI 詳細分析:</div>
                    <div className="text-sm text-green-700 bg-white p-2 rounded border">
                      <pre className="whitespace-pre-wrap text-xs">
                        {JSON.stringify(sentence.detailedAnalysis, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Visualization Tab
export const VisualizationTab = ({ passageStructures, expandedItems, toggleExpand, downloadHTML, analysisStats, inputText }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-800">Passage 句型結構樹狀視覺化</h3>
        <button
          onClick={downloadHTML}
          disabled={passageStructures.length === 0 || analysisStats.isAnalyzing}
          className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
            passageStructures.length > 0 && !analysisStats.isAnalyzing
              ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl transform hover:scale-105'
              : 'bg-gray-400 text-gray-600 cursor-not-allowed shadow-md'
          }`}
          style={{
            position: 'relative',
            zIndex: 10,
            minWidth: '160px',
            border: '2px solid transparent'
          }}
        >
          <Download size={18} className="mr-2" />
          {analysisStats.isAnalyzing ? '分析中...' : '下載 HTML 報告'}
        </button>
      </div>
      
      {/* Analysis Statistics */}
      {passageStructures.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
            <Zap size={20} className="mr-2 text-blue-600" />
            分析統計
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">分析時間</span>
                <Clock size={16} className="text-blue-600" />
              </div>
              <div className="text-xl font-bold text-blue-600 mt-1">
                {analysisStats.timeElapsed}s
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-purple-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">總 Token</span>
                <Zap size={16} className="text-purple-600" />
              </div>
              <div className="text-xl font-bold text-purple-600 mt-1">
                {analysisStats.tokensUsed}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">分析句子</span>
                <BarChart3 size={16} className="text-green-600" />
              </div>
              <div className="text-xl font-bold text-green-600 mt-1">
                {analysisStats.sentencesAnalyzed}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-orange-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">視覺化結構</span>
                <TreePine size={16} className="text-orange-600" />
              </div>
              <div className="text-xl font-bold text-orange-600 mt-1">
                {passageStructures.length}
              </div>
            </div>
          </div>
          
          {/* Cost Information */}
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-4">
            <h5 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
              <span className="text-red-600 mr-2">💰</span>
              成本分析
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <div className="text-sm text-gray-600">輸入 Token</div>
                <div className="text-lg font-bold text-red-600">
                  {analysisStats.inputTokens}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <div className="text-sm text-gray-600">輸出 Token</div>
                <div className="text-lg font-bold text-red-600">
                  {analysisStats.outputTokens}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <div className="text-sm text-gray-600">總成本 (¥)</div>
                <div className="text-lg font-bold text-red-600">
                  {analysisStats.cost.toFixed(4)}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <div className="text-sm text-gray-600">計費時段</div>
                <div className="text-lg font-bold text-red-600">
                  {analysisStats.isStandardTime ? '標準時段' : '優惠時段'}
                </div>
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-600">
              {analysisStats.isStandardTime 
                ? '標準時段 (08:30-00:30): 輸入 2元/百萬tokens，輸出 8元/百萬tokens'
                : '優惠時段 (00:30-08:30): 輸入 1元/百萬tokens，輸出 4元/百萬tokens (5折)'
              }
            </div>
          </div>
        </div>
      )}
      
      {passageStructures.length === 0 ? (
        <div className="text-center py-8 text-gray-500 bg-blue-50 rounded-lg border">
          <TreePine size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-4">請先在「句型分析」頁面輸入並分析段落，然後查看樹狀視覺化結果。</p>
          <div className="mt-6 p-6 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
            <div className="flex items-center justify-center mb-3">
              <Download size={24} className="text-yellow-600 mr-2" />
              <span className="text-lg font-semibold text-yellow-800">下載按鈕位置</span>
            </div>
            <p className="text-sm text-yellow-800 text-center">
              💡 <strong>提示：</strong>完成分析後，右上角的「下載 HTML 報告」按鈕將會啟用，讓您下載完整的分析報告。
            </p>
            <div className="mt-4 text-xs text-yellow-700">
              <p>📍 按鈕位置：頁面右上角，標題「Passage 句型結構樹狀視覺化」的右側</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {passageStructures.map((structure, index) => (
            <div key={structure.id} className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleExpand(`tree-${index}`)}
                className="flex items-center w-full text-left p-4 hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center">
                  {expandedItems[`tree-${index}`] ? 
                    <ChevronDown size={16} className="mr-2 text-blue-600" /> : 
                    <ChevronRight size={16} className="mr-2 text-blue-600" />
                  }
                  <span className="text-blue-700 font-medium">{structure.sentence}</span>
                </div>
              </button>

              {expandedItems[`tree-${index}`] && (
                <div className="px-4 pb-4 space-y-3 border-t border-blue-200 bg-white">
                                     <div className="pt-3">
                     <div className="flex items-start space-x-2">
                       <span className="text-sm font-bold text-gray-700 min-w-20">主句句型：</span>
                       <div className="flex flex-wrap items-center gap-2">
                         <span className="text-orange-600 font-medium">
                           {structure.analysis.mainType}
                         </span>
                         <span className="text-gray-600 text-sm">
                           {structure.analysis.basicStructure}
                         </span>
                       </div>
                     </div>
                     {structure.analysis.legendStructure && (
                       <div className="ml-6 mt-2">
                         <div className="flex items-start space-x-2">
                           <span className="text-sm font-bold text-gray-700 min-w-12">Legend結構：</span>
                           <div className="flex flex-wrap gap-1">
                             {structure.analysis.legendStructure
                               .split('+')
                               .map((part, index) => {
                                 const trimmed = part.trim();
                                 if (!trimmed) return null;
                                 
                                 // Extract the grammatical function from parentheses
                                 const match = trimmed.match(/(.+)\(([^)]+)\)/);
                                 if (match) {
                                   const [, word, func] = match;
                                   return (
                                     <span key={index} className="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">
                                       <span className="text-blue-900">{word.trim()}</span>
                                       <span className="text-blue-600 ml-1">({func})</span>
                                     </span>
                                   );
                                 }
                                 
                                 return (
                                   <span key={index} className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-mono">
                                     {trimmed}
                                   </span>
                                 );
                               })}
                           </div>
                         </div>
                       </div>
                     )}
                   </div>

                  <div className="ml-6">
                    <div className="flex items-start space-x-2">
                      <span className="text-sm font-bold text-gray-700 min-w-12">細分：</span>
                      <span className="text-sm text-gray-700">{structure.analysis.details}</span>
                    </div>
                  </div>

                  {structure.analysis.subclauses && structure.analysis.subclauses.length > 0 && (
                    <div className="ml-6">
                      <div className="flex items-start space-x-2">
                        <span className="text-sm font-bold text-gray-700 min-w-12">細細分：</span>
                        <div className="text-sm text-gray-700">
                          {Array.isArray(structure.analysis.subclauses) 
                            ? structure.analysis.subclauses
                                .map(clause => {
                                  if (typeof clause === 'string') return clause;
                                  if (typeof clause === 'object' && clause !== null) {
                                    // If it has clause, pattern, analysis keys, show a summary
                                    if ('clause' in clause && 'pattern' in clause && 'analysis' in clause) {
                                      return `Clause: ${clause.clause}, Pattern: ${clause.pattern}, Analysis: ${typeof clause.analysis === 'string' ? clause.analysis : JSON.stringify(clause.analysis)}`;
                                    }
                                    // Otherwise, try to show name/type or fallback to JSON
                                    const nameOrType = clause.name || clause.type;
                                    if (nameOrType) return String(nameOrType);
                                    try {
                                      return JSON.stringify(clause);
                                    } catch (e) {
                                      return String(clause);
                                    }
                                  }
                                  return String(clause);
                                })
                                .join('、')
                            : String(structure.analysis.subclauses)
                          }
                        </div>
                      </div>
                    </div>
                  )}

                  {structure.conditionClause && (
                    <div className="ml-6">
                      <button
                        onClick={() => toggleExpand(`tree-${index}-condition`)}
                        className="flex items-center text-sm font-bold text-gray-700 mb-2"
                      >
                        {expandedItems[`tree-${index}-condition`] ? 
                          <ChevronDown size={14} className="mr-1" /> : 
                          <ChevronRight size={14} className="mr-1" />
                        }
                        <span>條件從句：</span>
                      </button>
                      
                      {expandedItems[`tree-${index}-condition`] && (
                        <div className="ml-6 space-y-1">
                          <div className="flex items-start space-x-2">
                            <span className="text-orange-600 font-medium text-sm">
                              {structure.conditionClause.type}
                            </span>
                            <span className="text-gray-600 text-sm">
                              ({structure.conditionClause.content})
                            </span>
                          </div>
                          <div className="ml-6 text-sm text-gray-700">
                            {structure.conditionClause.description}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Quiz Tab
export const QuizTab = ({ quizQuestions, currentQuizIndex, quizAnswers, handleQuizAnswer, nextQuizQuestion, prevQuizQuestion }) => {
  if (quizQuestions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 bg-blue-50 rounded-lg border">
        <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
        <p>請先在「句型分析」頁面輸入並分析段落，然後查看互動練習測驗。</p>
      </div>
    );
  }

  const currentQuestion = quizQuestions[currentQuizIndex];
  const userAnswer = quizAnswers[currentQuestion.id];
  const hasAnswered = userAnswer !== undefined;

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">句型結構互動練習</h2>
      
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <div className="text-sm text-gray-600 mb-4">
          第 {currentQuizIndex + 1} / {quizQuestions.length} 題
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-gray-800 font-medium leading-relaxed">
            {currentQuestion.question}
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {currentQuestion.options.map((option, index) => (
            <label
              key={index}
              className={`block cursor-pointer transition-colors ${
                hasAnswered && option.isCorrect 
                  ? 'bg-green-100 border-green-300' 
                  : hasAnswered && userAnswer === index && !option.isCorrect
                  ? 'bg-red-100 border-red-300'
                  : userAnswer === index
                  ? 'bg-blue-100 border-blue-300'
                  : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
              } border rounded-lg p-4`}
            >
              <input
                type="radio"
                name={`quiz-${currentQuestion.id}`}
                value={index}
                checked={userAnswer === index}
                onChange={() => handleQuizAnswer(currentQuestion.id, index)}
                className="sr-only"
              />
              <span className="text-gray-800">{option.text}</span>
            </label>
          ))}
        </div>

        {hasAnswered && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-6">
            <div className="flex items-start">
              <CheckCircle className="text-green-600 mr-2 mt-1" size={16} />
              <div>
                <div className="font-medium text-gray-800 mb-2">
                  ✅ 正確！
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {currentQuestion.explanation}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between">
          {currentQuizIndex > 0 && (
            <button
              onClick={prevQuizQuestion}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              上一題
            </button>
          )}
          <div className="flex-1"></div>
          {currentQuizIndex < quizQuestions.length - 1 ? (
            <button
              onClick={nextQuizQuestion}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              下一題
            </button>
          ) : hasAnswered && (
            <button
              onClick={() => alert('測驗完成！')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              完成測驗
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Rewrite Tab
export const RewriteTab = ({ 
  rewriteExercises, 
  currentRewriteIndex, 
  rewriteAnswers, 
  selectedRewriteType, 
  showExample, 
  checkResult, 
  rewriteOptions,
  handleRewriteAnswer,
  handleRewriteTypeChange,
  checkRewriteAnswer,
  showRewriteExample,
  clearRewriteAnswer,
  nextRewriteExercise,
  prevRewriteExercise
}) => {
  if (rewriteExercises.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 bg-blue-50 rounded-lg border">
        <Edit3 size={48} className="mx-auto mb-4 opacity-50" />
        <p>請先在「句型分析」頁面輸入並分析段落，然後查看句型改寫練習。</p>
      </div>
    );
  }

  const currentExercise = rewriteExercises[currentRewriteIndex];
  const userAnswer = rewriteAnswers[currentExercise.id] || '';

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">互動句型改寫練習</h2>
      
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        {/* 題號 */}
        <div className="text-sm text-gray-600 mb-4">
          第 {currentRewriteIndex + 1} / {rewriteExercises.length} 題
        </div>

        {/* 原句 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-gray-800 font-medium leading-relaxed">
            {currentExercise.sentence}
          </p>
        </div>

        {/* 目標句型選擇器 */}
        <div className="mb-4 relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            選擇要改寫的句型：
          </label>
          <select 
            value={selectedRewriteType}
            onChange={(e) => handleRewriteTypeChange(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-800"
          >
            {rewriteOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* 改寫指引 */}
        {selectedRewriteType && currentExercise.instructions[selectedRewriteType] && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="font-medium text-gray-800 mb-1">改寫指引：</div>
            <p className="text-sm text-gray-700">{currentExercise.instructions[selectedRewriteType]}</p>
          </div>
        )}

        {/* 輸入區域 */}
        <div className="mb-6">
          <textarea
            value={userAnswer}
            onChange={(e) => handleRewriteAnswer(currentExercise.id, e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="3"
            placeholder="請在這裡輸入您的改寫句子..."
          />
        </div>

        {/* 三個功能按鈕 */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={checkRewriteAnswer}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            檢查
          </button>
          <button
            onClick={showRewriteExample}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            顯示範例
          </button>
          <button
            onClick={clearRewriteAnswer}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            清空
          </button>
        </div>

        {/* 檢查結果顯示 */}
        {checkResult && !showExample && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="font-medium text-gray-800 mb-1">檢查結果：</div>
            <p className="text-sm text-gray-700">{checkResult}</p>
          </div>
        )}

        {/* 範例顯示 */}
        {showExample && selectedRewriteType && currentExercise.examples[selectedRewriteType] && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="font-medium text-gray-800 mb-1">範例：</div>
            <p className="text-gray-700">{currentExercise.examples[selectedRewriteType]}</p>
          </div>
        )}

        {/* 導航按鈕 */}
        <div className="flex justify-between">
          {currentRewriteIndex > 0 && (
            <button
              onClick={prevRewriteExercise}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              上一題
            </button>
          )}
          <div className="flex-1"></div>
          {currentRewriteIndex < rewriteExercises.length - 1 && (
            <button
              onClick={nextRewriteExercise}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              下一題
            </button>
          )}
        </div>
      </div>
    </div>
  );
}; 